import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Typography, Segmented } from "antd";
import { BaseFilterItem } from "components/UI/BaseFilter/BaseFilterItem/BaseFilterItem.tsx";
import { FilterGroup } from "components/UI/BaseFilter/FilterGroup/FilterGroup.tsx";
import { useBaseFilter } from "components/UI/BaseFilter/hooks/useBaseFilter.ts";
import { InsertControls } from "components/UI/BaseFilter/InsertControls/InsertControls.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";

type OfferData = App.Data.OfferData;

interface BaseFilterProps {
    offer?: OfferData;
    cascade?: { id: string };
}

export const BaseFilter = ({ offer, cascade }: BaseFilterProps) => {
    const baseFilterUtils = useBaseFilter(offer);

    const availableFilterColumns = baseFilterUtils.getAvailableFilterColumns();
    const { t } = usePaldockTranslation("ui.offer-filter");

    const filters = cascade
        ? baseFilterUtils.getCascadeFilters(cascade.id)
        : offer
          ? baseFilterUtils.getOfferFilters(offer)
          : [];

    const filterGroups = cascade
        ? baseFilterUtils.getCascadeFilterGroups(cascade.id)
        : offer
          ? baseFilterUtils.getOfferFilterGroups(offer)
          : [];

    const clearFilters = () => {
        if (cascade) {
            baseFilterUtils.clearCascadeFilters(cascade.id);
        } else if (offer) {
            baseFilterUtils.clearFilters(offer);
        }
    };

    const updateLogicalOperator = (filterId: number, logicalOperator: "and" | "or") => {
        if (cascade) {
            baseFilterUtils.updateCascadeFilter(cascade.id, filterId, "logicalOperator", logicalOperator);
        } else if (offer) {
            baseFilterUtils.updateFilter(offer, filterId, "logicalOperator", logicalOperator);
        }
    };

    const updateGroupLogicalOperator = (groupId: number, logicalOperator: "and" | "or") => {
        if (cascade) {
            baseFilterUtils.updateCascadeFilterGroup(cascade.id, groupId, "logicalOperator", logicalOperator);
        } else if (offer) {
            baseFilterUtils.updateFilterGroup(offer, groupId, "logicalOperator", logicalOperator);
        }
    };

    const addFilterGroup = () => {
        if (cascade) {
            baseFilterUtils.addCascadeFilterGroup(cascade.id);
        } else if (offer) {
            baseFilterUtils.addFilterGroup(offer);
        }
    };

    const allItems = [
        ...filters.map((filter) => ({ type: "filter" as const, data: filter })),
        ...filterGroups.map((group) => ({ type: "group" as const, data: group })),
    ].sort((a, b) => a.data.order - b.data.order);

    const hasAnyFilters = filters.length > 0 || filterGroups.length > 0;

    const cardClassName = cascade
        ? "mt-2 rounded-lg w-full border-none bg-blue-50/50"
        : "mt-4 rounded-lg w-full border-none bg-[#00000005]";

    const titleLevel = cascade ? 5 : 4;
    const titleText = cascade ? t("title_cascade") : t("title");

    return (
        <Card className={cardClassName}>
            <Row justify="space-between" align="middle">
                <Col>
                    <Typography.Title level={titleLevel}>{titleText}</Typography.Title>
                </Col>
                <Col>
                    <Space>
                        <Button type={"link"} icon={<PlusOutlined />} onClick={addFilterGroup}>
                            {t("actions.add_group")}
                        </Button>
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => {
                                if (cascade) {
                                    baseFilterUtils.addCascadeFilter(cascade.id);
                                } else if (offer) {
                                    baseFilterUtils.addFilter(offer);
                                }
                            }}
                        >
                            {t("actions.add_filter")}
                        </Button>
                        <Button icon={<CloseOutlined />} onClick={clearFilters}>
                            {t("labels.clear")}
                        </Button>
                    </Space>
                </Col>
            </Row>

            <div className="mt-4 p-0">
                {/* Insertion point at the beginning */}
                {hasAnyFilters && (
                    <InsertControls
                        position="between"
                        onAddFilter={() => {
                            if (cascade) {
                                baseFilterUtils.addCascadeFilter(cascade.id, 0);
                            } else if (offer) {
                                baseFilterUtils.addFilter(offer, 0);
                            }
                        }}
                        onAddGroup={() => {
                            if (cascade) {
                                baseFilterUtils.addCascadeFilterGroup(cascade.id, 0);
                            } else if (offer) {
                                baseFilterUtils.addFilterGroup(offer, 0);
                            }
                        }}
                    />
                )}

                {allItems.map((item, index) => (
                    <div key={`${item.type}-${item.data.id}`}>
                        <div className="mb-2">
                            {item.type === "filter" ? (
                                <BaseFilterItem
                                    filter={item.data}
                                    offer={offer}
                                    cascade={cascade}
                                    availableFilterColumns={availableFilterColumns}
                                />
                            ) : (
                                <FilterGroup group={item.data} offer={offer} cascade={cascade} />
                            )}
                        </div>
                        {/* Show logical operator between items (not after the last one) */}
                        {index < allItems.length - 1 && (
                            <div className="flex justify-center my-4">
                                <Segmented
                                    value={item.data.logicalOperator}
                                    onChange={(value) => {
                                        if (item.type === "filter") {
                                            updateLogicalOperator(item.data.id, value as "and" | "or");
                                        } else {
                                            updateGroupLogicalOperator(item.data.id, value as "and" | "or");
                                        }
                                    }}
                                    options={[
                                        { label: t("labels.and"), value: "and" },
                                        { label: t("labels.or"), value: "or" },
                                    ]}
                                    style={{ width: "250px" }}
                                    className="[&_.ant-segmented-item]:!w-[125px]"
                                />
                            </div>
                        )}
                        {/* Insertion point after each item */}
                        <InsertControls
                            position="between"
                            onAddFilter={() => {
                                if (cascade) {
                                    baseFilterUtils.addCascadeFilter(cascade.id, index + 1);
                                } else if (offer) {
                                    baseFilterUtils.addFilter(offer, index + 1);
                                }
                            }}
                            onAddGroup={() => {
                                if (cascade) {
                                    baseFilterUtils.addCascadeFilterGroup(cascade.id, index + 1);
                                } else if (offer) {
                                    baseFilterUtils.addFilterGroup(offer, index + 1);
                                }
                            }}
                        />
                    </div>
                ))}

                {/* If no items, show insertion controls in center */}
                {!hasAnyFilters && (
                    <InsertControls
                        position="end"
                        onAddFilter={() => {
                            if (cascade) {
                                baseFilterUtils.addCascadeFilter(cascade.id);
                            } else if (offer) {
                                baseFilterUtils.addFilter(offer);
                            }
                        }}
                        onAddGroup={() => {
                            if (cascade) {
                                baseFilterUtils.addCascadeFilterGroup(cascade.id);
                            } else if (offer) {
                                baseFilterUtils.addFilterGroup(offer);
                            }
                        }}
                    />
                )}
            </div>
        </Card>
    );
};
